import React from 'react';
import { QuoteIcon } from 'lucide-react';
interface TestimonialCardProps {
  text: string;
  headline?: string;
  headlineNode?: React.ReactNode;
  highlight?: boolean;
  profileImage?: string;
  name?: string;
}
const importantPhrase = "they are likely in the top 5 most important people I've ever met";
export function TestimonialCard({
  text,
  headline,
  headlineNode,
  highlight = false,
  profileImage,
  name = 'Peptide Science Institute Transformation Client'
}: TestimonialCardProps) {
  const paragraphs = text.split('\n\n').map(cleanBoundaryQuotes);
  const resultsIndex = paragraphs.findIndex(paragraph => paragraph.trim() === 'RESULTS:');
  return <figure className="space-y-6" data-id="element-488" data-mp="3434jfxmzxxzc">
      {headlineNode ? <div className="mx-auto max-w-3xl text-center font-display text-xl font-bold leading-snug text-ink md:text-2xl" data-id="element-489" data-mp="385j48na877t9">
          {headlineNode}
        </div> : headline ? <p className="mx-auto max-w-3xl text-center font-display text-xl font-bold leading-snug text-ink md:text-2xl" data-id="element-490" data-mp="1ds9pafywuu3m">
          {renderHeadline(headline)}
        </p> : null}

      <div className={`relative overflow-hidden rounded-[2rem] border bg-white shadow-[0_20px_55px_rgba(15,23,42,0.12)] ${highlight ? 'border-teal-300 ring-1 ring-teal-200' : 'border-black/[0.07]'}`} data-id="element-491" data-mp="huxi9vnxj6uv">
        <div className="h-1.5 w-full bg-teal-500" aria-hidden="true" data-id="element-492" data-mp="80x12vfdib5s" />

        <div className="p-7 md:p-10" data-id="element-493" data-mp="35hsahw835g8">
          <figcaption className="flex items-center gap-4 border-b border-black/[0.07] pb-6" data-id="element-494" data-mp="10row3pkpctrj">
            {profileImage ? <img src={profileImage} alt={name} width={80} height={80} loading="lazy" decoding="async" className="h-16 w-16 flex-shrink-0 rounded-2xl border-4 border-teal-50 bg-gray-100 object-cover shadow-md md:h-20 md:w-20" data-id="element-495" data-mp="vsvi7w1i70qu" /> : <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-teal-50" data-id="element-496" data-mp="lv7lx1o91l6k">
                <QuoteIcon className="h-6 w-6 text-teal-600" aria-hidden="true" data-id="element-497" data-mp="3bkt2f7a6gmbn" />
              </span>}
            <div className="min-w-0 text-left" data-id="element-498" data-mp="36jlzpmmw6dbu">
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-teal-700" data-id="element-499" data-mp="36mzx0ggp7wcf">
                Client Testimonial
              </p>
              <p className="mt-1 text-sm font-bold leading-snug text-ink md:text-base" data-id="element-500" data-mp="22lrzpjvdaho4">
                {name}
              </p>
            </div>
          </figcaption>

          <div className="mt-7 space-y-5" data-id="element-501" data-mp="1tlv5z3u4esm7">
            {paragraphs.map((paragraph, index) => {
            const isResults = paragraph.trim() === 'RESULTS:';
            const isResultDetail = resultsIndex >= 0 && index > resultsIndex && index <= resultsIndex + 4;
            const hasImportantPhrase = paragraph.includes(importantPhrase);
            if (isResults) {
              return <div key={`${paragraph}-${index}`} className="flex items-center gap-3 pt-2" data-id="element-502" data-mp="15upsuwfkeefl">
                    <span className="h-px flex-1 bg-teal-200" data-id="element-503" data-mp="1at9c2p2rxc49" />
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-teal-700" data-id="element-504" data-mp="vywgpe5rcmyc">
                      Results
                    </p>
                    <span className="h-px flex-1 bg-teal-200" data-id="element-505" data-mp="hbe8w6wwfc7p" />
                  </div>;
            }
            if (isResultDetail) {
              return <div key={`${paragraph}-${index}`} className="flex items-start gap-3 rounded-2xl border border-teal-100 bg-teal-50 p-5" data-id="element-506" data-mp="dbduundpp316">
                    <span className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-teal-500" data-id="element-507" data-mp="r7h9v9g0l4t2" />
                    <p className="text-lg font-bold leading-relaxed text-ink md:text-xl" data-id="element-508" data-mp="mctrlsblt6mx">
                      {paragraph}
                    </p>
                  </div>;
            }
            return <p key={`${paragraph}-${index}`} className="text-lg font-normal leading-[1.75] text-ink md:text-xl" data-id="element-509" data-mp="w8oj7cxt2c1u" data-mpi={index}>
                  {hasImportantPhrase ? renderImportantPhrase(paragraph) : paragraph}
                </p>;
          })}
          </div>
        </div>
      </div>
    </figure>;
}
function renderHeadline(headline: string) {
  const phrase = headline.includes('"borderline miracle."') ? '"borderline miracle."' : headline.includes('"borderline miracle"') ? '"borderline miracle"' : null;
  if (!phrase) return headline;
  const [before, after] = headline.split(phrase);
  const cleanPhrase = phrase.replace(/\."$/, '"');
  return <>
      {before}
      <span className="mt-2 block text-2xl font-black text-teal-600 md:text-3xl" data-id="element-510" data-mp="3i7op92puudsy">
        {cleanPhrase}
      </span>
      {after}
    </>;
}
function renderImportantPhrase(paragraph: string) {
  const [before, after] = paragraph.split(importantPhrase);
  return <>
      {before}
      <span className="my-4 block rounded-2xl border border-teal-200 bg-teal-50 px-5 py-4 font-display text-2xl font-black leading-tight text-teal-700 md:text-3xl" data-id="element-511" data-mp="2ui4207mo4udv">
        {importantPhrase}
      </span>
      {after}
    </>;
}
function cleanBoundaryQuotes(paragraph: string) {
  return paragraph.trim().replace(/^["“]+/, '').replace(/["”]+$/, '');
}